import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertCircle, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useObligaciones } from "@/hooks/useObligaciones";

const DAYS_AHEAD = 14;

const tipoToRoute: Record<string, string> = {
  Ambiental: "/gestor-ambiental",
  Biótico: "/gestor-ambiental",
  Abiótico: "/gestor-ambiental",
  Social: "/gestor-social",
  SST: "/gestor-sst",
};

function daysUntil(dateStr: string) {
  if (!dateStr) return Infinity;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

export function NotificationsBell() {
  const { obligaciones, loading } = useObligaciones();

  const alertas = useMemo(() => {
    return obligaciones
      .filter((o) => o.estado !== "Cumplido" && o.vencimiento)
      .map((o) => ({ ...o, _days: daysUntil(o.vencimiento) }))
      .filter((o) => o._days <= DAYS_AHEAD)
      .sort((a, b) => a._days - b._days);
  }, [obligaciones]);

  const vencidas = alertas.filter((a) => a._days < 0);
  const proximas = alertas.filter((a) => a._days >= 0);
  const total = alertas.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-lg relative"
          aria-label="Notificaciones"
        >
          <Bell className="h-4 w-4" />
          {total > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
              {total > 9 ? "9+" : total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Notificaciones</h3>
          <p className="text-xs text-muted-foreground">
            Tareas vencidas o próximas a vencer ({DAYS_AHEAD} días)
          </p>
        </div>

        <ScrollArea className="max-h-96">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Cargando…
            </div>
          ) : total === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              Sin alertas pendientes
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {[...vencidas, ...proximas].map((o) => {
                const overdue = o._days < 0;
                const route = tipoToRoute[o.tipo] ?? "/home";
                return (
                  <li key={o.id}>
                    <Link
                      to={route}
                      className="flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                    >
                      <div
                        className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                          overdue
                            ? "bg-destructive/10 text-destructive"
                            : "bg-amber-500/10 text-amber-600"
                        }`}
                      >
                        {overdue ? (
                          <AlertCircle className="h-4 w-4" />
                        ) : (
                          <Clock className="h-4 w-4" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium break-words leading-snug">
                          {o.nombre_programa}
                        </p>
                        <p className="text-xs text-muted-foreground break-words mt-0.5">
                          {o.codigo} · {o.tipo} · {o.responsable_interno || "Sin responsable"}
                        </p>
                        <p
                          className={`text-xs mt-0.5 font-medium ${
                            overdue ? "text-destructive" : "text-amber-600"
                          }`}
                        >
                          {overdue
                            ? `Vencida hace ${Math.abs(o._days)} día${Math.abs(o._days) === 1 ? "" : "s"}`
                            : o._days === 0
                            ? "Vence hoy"
                            : `Vence en ${o._days} día${o._days === 1 ? "" : "s"}`}
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>

        <div className="px-4 py-2 border-t border-border flex justify-between items-center gap-2">
          <Link
            to="/gestor-ambiental"
            className="text-xs text-primary hover:underline"
          >
            Ver obligaciones ambientales
          </Link>
          <Link
            to="/home"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Ir al dashboard
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
